/**
 * web/src/components/gates/TrackerChangesPanel.tsx
 *
 * "Agent's Tracker changes" panel shown on the supplementary-review data
 * gate. Renders the before/after diff of the cells the agent wrote to the
 * Sponsorship Tracker (step 2), with a Revert button that restores the row
 * to its pre-agent values via POST /api/workflows/:id/tracker/revert.
 *
 * Revert uses a two-click inline confirm (no modal) — first click arms,
 * second click fires, and clicking anywhere else / re-toggling disarms.
 * Once reverted (live `revertedAt` from workflow.context wins over the
 * static gateData snapshot), the button is replaced by a "reverted" note.
 */
import { useState } from 'react';
import { Button } from '../shared/Button';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import type { TrackerChanges } from '../../api/types';

/**
 * Renders a Tracker cell value for the diff table. Formula cells (the
 * `=HYPERLINK` Contracts cell) collapse to a short tag, blanks render as
 * an em-dash, everything else is stringified.
 */
function cellText(v: unknown): string {
  if (v == null || v === '') return '—';
  const s = String(v);
  if (s.trimStart().startsWith('=')) return '(formula)';
  return s;
}

/**
 * Before/after diff of the agent's Tracker write + the Revert action.
 *
 * @param changes the gateData.trackerChanges block (static, from gate build)
 * @param revertedAt live revert timestamp from workflow.context (wins over
 *        the static block — the gateData blob never updates after the gate opens)
 * @param revertedBy live revert actor name from workflow.context
 * @param canRevert whether the viewer may revert (mirrors gate approval permission)
 * @param isReverting mutation in-flight flag
 * @param onRevert fires the revert mutation
 * @sideEffects none — presentation + local arm/disarm state only
 * @idempotent yes
 */
export function TrackerChangesPanel({
  changes,
  revertedAt,
  revertedBy,
  canRevert,
  isReverting,
  onRevert,
  className,
}: {
  changes: TrackerChanges;
  revertedAt: string | null;
  revertedBy: string | null;
  canRevert: boolean;
  isReverting: boolean;
  onRevert: () => void;
  className?: string;
}) {
  const [armed, setArmed] = useState(false);
  const isReverted = revertedAt != null;
  // Only show rows where the agent actually changed something — identical
  // before/after (re-writes of the same value) are noise for the reviewer.
  const changed = changes.columns.filter((c) => cellText(c.before) !== cellText(c.after));
  const rows = changed.length > 0 ? changed : changes.columns;

  return (
    // Collapsed by default — the agent-diff + Revert is a review-on-demand
    // affordance, not the focus of the gate. The summary line is the toggle:
    // one click expands the before/after table and the Revert button.
    <details className={cn('group rounded-md border border-border bg-bg-soft', className)}>
      <summary
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer select-none list-none',
          'marker:hidden [&::-webkit-details-marker]:hidden text-[12px] text-text-muted',
        )}
      >
        <Icon
          name="chevron-right"
          size={12}
          className="shrink-0 transition-transform group-open:rotate-90"
        />
        <Icon name="info" size={12} className="shrink-0" />
        <span className="font-medium">
          The agent {changes.rowExisted ? 'updated' : 'created'} Tracker row {changes.rowNumber}
          {changed.length > 0 ? ` — ${changed.length} column${changed.length === 1 ? '' : 's'} changed` : ''}
        </span>
        <span className="text-text-faint">· review &amp; revert</span>
        <div className="flex-1" />
        {isReverted && (
          <span className="text-[11px] text-completed font-medium flex items-center gap-1">
            <Icon name="check" size={11} />
            Reverted
          </span>
        )}
      </summary>

      <div className="px-3 pb-3 pt-1">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="text-left text-[11px] text-text-muted">
              <th className="font-medium py-1 pr-3">Column</th>
              <th className="font-medium py-1 pr-3">Before (pre-agent)</th>
              <th className="font-medium py-1">Agent wrote</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.header} className="border-t border-border/60 align-top">
                <td className="py-1.5 pr-3 text-text-muted whitespace-nowrap">{c.label}</td>
                <td className="py-1.5 pr-3 text-text break-words max-w-[200px]">
                  {cellText(c.before)}
                </td>
                <td className="py-1.5 text-text break-words max-w-[200px]">{cellText(c.after)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
          {isReverted ? (
            <span className="text-[11px] text-text-muted">
              Tracker row restored to its pre-agent values
              {revertedBy ? ` by ${revertedBy}` : ''}
              {` · ${new Date(revertedAt).toLocaleString()}`}
            </span>
          ) : canRevert ? (
            <>
              <Button
                type="button"
                size="sm"
                variant={armed ? 'danger' : 'ghost'}
                disabled={isReverting}
                onClick={() => {
                  if (!armed) {
                    setArmed(true);
                    return;
                  }
                  setArmed(false);
                  onRevert();
                }}
                title="Restore the Tracker row to the values it held before the agent wrote it"
              >
                <Icon name="retry" size={12} />
                {isReverting
                  ? 'Reverting…'
                  : armed
                    ? 'Click again to confirm revert'
                    : 'Revert agent changes'}
              </Button>
              {armed && !isReverting && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setArmed(false)}>
                  Cancel
                </Button>
              )}
              <span className="text-[11px] text-text-muted">
                {changes.rowExisted
                  ? 'Restores the cells above to their pre-agent values in the live Sheet.'
                  : 'The row was new — reverting blanks the cells the agent wrote.'}
              </span>
            </>
          ) : null}
        </div>
      </div>
    </details>
  );
}
